// Removed unused imports - no longer needed after switching to API

// API endpoint for our Spotify proxy service
const API_BASE_URL = 'https://bears.playlistpartner.com/api/spotify';

export interface SpotifyTrack {
  id: string;
  name: string;
  artists: { name: string; id: string }[];
  album: {
    name: string;
    images: { url: string }[];
  };
  external_urls: {
    spotify: string;
  };
}

export interface SpotifyPlaylist {
  id: string;
  name: string;
  description: string;
  images: { url: string }[];
  followers: { total: number };
  owner: {
    id: string;
    display_name: string;
    external_urls: {
      spotify: string;
    };
  };
  tracks: {
    total: number;
  };
  curator_email: string | null;
}

function cleanSpotifyUrl(url: string): string {
  try {
    // Handle Spotify URI format
    if (url.startsWith('spotify:')) {
      const parts = url.split(':');
      return `https://open.spotify.com/${parts[1]}/${parts[2]}`;
    }

    // Remove query parameters and hash
    const urlObj = new URL(url);
    return `https://open.spotify.com${urlObj.pathname}`;
  } catch (error) {
    return url; // Return original if URL parsing fails
  }
}

export function formatSpotifyUrl(type: 'track' | 'playlist' | 'user', id: string): string {
  return `https://open.spotify.com/${type}/${id}`;
}

export function extractSpotifyId(url: string, type: 'track' | 'playlist' | 'user'): string | null {
  if (!url) return null;
  
  try {
    // Clean the URL first
    const cleanedUrl = cleanSpotifyUrl(url);

    // Handle both URL and URI formats
    const patterns = [
      new RegExp(`${type}/([a-zA-Z0-9]+)`), // URL format
      new RegExp(`${type}:([a-zA-Z0-9]+)`)   // URI format
    ];

    for (const pattern of patterns) {
      const match = cleanedUrl.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  } catch (error) {
    console.error('Failed to extract ID:', error);
    return null;
  }
}


// DEPRECATED: getPlaylistDetails removed - admin functionality being replaced with proper backend
// This was only used in admin pages (PlaylistForm, PlaylistSync, PlaylistSyncModal)
// which are being deprecated in favor of server-side implementation

export class SearchError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function searchTracks(query: string): Promise<SpotifyTrack[]> {
  if (!query.trim()) return [];

  try {
    const response = await fetch(
      `${API_BASE_URL}/search?q=${encodeURIComponent(query)}&limit=5`
    );

    if (!response.ok) {
      if (response.status === 429) {
        const data = await response.json();
        throw new SearchError(data.message || 'Rate limit exceeded. Please try again later.', 429);
      }
      throw new SearchError(`Search failed: ${response.status} ${response.statusText}`, response.status);
    }

    const data = await response.json();
    
    // Our API returns tracks directly in the response
    if (!data.tracks) {
      throw new Error('Invalid response format from API');
    }

    return data.tracks;
  } catch (error) {
    console.error('Failed to search tracks:', error);
    throw error instanceof Error ? error : new Error('Failed to search tracks');
  }
}

export async function getTrackById(trackId: string): Promise<SpotifyTrack | null> {
  if (!trackId) return null;

  try {
    const response = await fetch(
      `${API_BASE_URL}/track/${encodeURIComponent(trackId)}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      if (response.status === 429) {
        const data = await response.json();
        throw new Error(data.message || 'Rate limit exceeded. Please try again later.');
      }
      throw new Error(`Failed to fetch track: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.id || !data.name || !data.artists || !data.album) {
      throw new Error('Invalid track data received from API');
    }

    return data;
  } catch (error) {
    console.error('Failed to fetch track:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch track data');
  }
}